import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getGetProjectsQueryKey,
  getGetReferenceMatchQueryKey,
  getListReferenceMatchDocumentRecordsQueryKey,
  useUpdateReferenceMatchDocumentRecord,
} from '@/entities/project/api/generated/api';
import type {
  ProjectReferenceMatchDetailResponse,
  ReferenceChatType,
} from '@/entities/project/api/generated/model';
import { toast } from '@/shared/ui';
import type { Judgement } from './types';

/** 判定（Judgement）を参照マッチ取得APIの is_user_selected へ変換する */
const judgementToUserSelected = (judgement: Judgement): boolean | null => {
  if (judgement === 'matched') return true;
  if (judgement === 'not-matched') return false;
  return null;
};

type Context = { previous: ProjectReferenceMatchDetailResponse | undefined };

/**
 * 事業参照マッチの判定（is_user_selected）を永続化するカスタムフック。
 *
 * - 判定 Select 変更時に PATCH `/projects/reference-matches/document-records/{id}` を呼ぶ
 * - 楽観更新（onMutate）で該当レコードの is_user_selected を即時反映し、ちらつきを防ぐ
 * - 失敗時（onError）はスナップショットへロールバックし、フォールバックトーストを表示する
 *   （`suppressToast: true` によりグローバルトーストを抑制しているため明示表示が必須）
 * - onSettled でサーバー状態と同期するため対象種別の参照マッチ取得をキャッシュ無効化する。
 *   さらに財源マッチング一覧（document-records）と財源別事業一覧（GET /projects）も、テーブルの
 *   行/件数/絞り込み・判定表示が古くならないよう無効化する（後者は判定を list レスポンスから導出するため）
 * - 成功時（onSuccess）は判定更新トーストを表示する
 */
export const useReferenceMatchJudgementMutation = ({
  projectId,
  referenceChatType,
}: {
  projectId: string;
  referenceChatType: ReferenceChatType;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = getGetReferenceMatchQueryKey(projectId, referenceChatType);

  const mutation = useUpdateReferenceMatchDocumentRecord<unknown, Context>({
    request: { suppressToast: true },
    mutation: {
      onMutate: async ({ documentRecordId, data }) => {
        await queryClient.cancelQueries({ queryKey });
        const previous = queryClient.getQueryData<ProjectReferenceMatchDetailResponse>(queryKey);

        if (previous) {
          queryClient.setQueryData<ProjectReferenceMatchDetailResponse>(queryKey, {
            ...previous,
            documents: previous.documents?.map((record) =>
              record.id === documentRecordId
                ? { ...record, is_user_selected: data.is_user_selected }
                : record,
            ),
          });
        }

        return { previous };
      },
      onSuccess: () => {
        toast.success(
          t('common.messages.update-success', {
            name: t('ai-matching-results.matching-skill.judgement-label'),
          }),
        );
      },
      onError: (_error, _variables, context) => {
        if (context?.previous) {
          queryClient.setQueryData(queryKey, context.previous);
        }
        toast.error(
          t('common.messages.update-failed', {
            name: t('ai-matching-results.matching-skill.judgement-label'),
          }),
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey });
        // 財源マッチング一覧テーブル（useListReferenceMatchDocumentRecords）も最新化する。
        // params 違いの全クエリを無効化するため prefix（params なし）のキーで指定する。
        queryClient.invalidateQueries({
          queryKey: getListReferenceMatchDocumentRecordsQueryKey(),
        });
        // 財源別事業一覧（useGetProjects）は判定を list レスポンスの reference_matches から導出するため、
        // 判定変更後に最新化する。query_store_id 違いの全クエリを prefix（params なし）で無効化する。
        queryClient.invalidateQueries({
          queryKey: getGetProjectsQueryKey(),
        });
      },
    },
  });

  const updateJudgement = (candidateId: string, next: Judgement) => {
    mutation.mutate({
      documentRecordId: candidateId,
      data: { is_user_selected: judgementToUserSelected(next) },
    });
  };

  return { updateJudgement, isUpdating: mutation.isPending };
};
